// ============================================================================
// PLAYER STATS SERVICE - Track player statistics in Firebase
// ============================================================================
// Tracks: unique players, playtime, wins, country, and more

import { FIREBASE_CONFIG } from '../config/firebase.config';

// Player statistics stored in Firestore
export interface PlayerStats {
  odplayerId: string;          // Unique player identifier (generated or from auth)
  callsign: string;           // Current player name
  country: string;            // ISO country code (e.g., "US", "TR")
  countryName: string;        // Full country name
  
  // Lifetime stats
  totalPlayTimeSeconds: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  totalKills: number;
  totalDeaths: number;
  totalAssists: number;
  
  // Hero preferences
  favoriteHero: string;
  heroStats: Record<string, HeroPlayStats>;
  
  // Timestamps
  firstSeenAt: any;           // Firestore Timestamp
  lastSeenAt: any;            // Firestore Timestamp
  lastSessionStart: any;      // For calculating session duration
}

export interface HeroPlayStats {
  gamesPlayed: number;
  gamesWon: number;
  kills: number;
  deaths: number;
  playTimeSeconds: number;
}

// Session data for current play session
export interface SessionData {
  sessionId: string;
  startTime: number;
  heroId: string;
  gamesPlayed: number;
  gamesWon: number;
  kills: number;
  deaths: number;
  assists: number;
}

// Global statistics for the webpage
export interface GlobalStats {
  totalUniquePlayers: number;
  totalGamesPlayed: number;
  totalPlayTimeHours: number;
  playersByCountry: Record<string, number>;
  lastUpdated: any;
}

class PlayerStatsServiceClass {
  private db: any = null;
  private firebase: any = null;
  private playerId: string = '';
  private currentSession: SessionData | null = null;
  private isInitialized = false;
  private countryInfo: { code: string; name: string } = { code: 'XX', name: 'Unknown' };
  
  private static readonly UNSAVED_PLAYTIME_KEY = 'cybergrid_unsavedPlayTime';

  // Generate or retrieve persistent player ID
  private getOrCreatePlayerId(): string {
    const storageKey = 'cybergrid_playerId';
    let playerId = localStorage.getItem(storageKey);
    
    if (!playerId) {
      playerId = `player_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(storageKey, playerId);
    }
    
    return playerId;
  }

  // Detect country from IP using free API
  private async detectCountry(): Promise<{ code: string; name: string }> {
    try {
      // Using ipapi.co (free, no API key required, 1000 requests/day)
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        return {
          code: data.country_code || 'XX',
          name: data.country_name || 'Unknown'
        };
      }
    } catch (e) {
      console.warn('[PlayerStats] Country detection failed:', e);
    }
    
    return { code: 'XX', name: 'Unknown' };
  }

  // Initialize the service
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Get player ID
      this.playerId = this.getOrCreatePlayerId();
      
      // Detect country (async, don't block)
      this.detectCountry().then(info => {
        this.countryInfo = info;
        console.log(`[PlayerStats] Country detected: ${info.name} (${info.code})`);
      });

      // Initialize Firebase
      const { initializeApp, getApps, getApp } = await import('firebase/app');
      const { 
        getFirestore, collection, doc, setDoc, getDoc, updateDoc, 
        increment, serverTimestamp, arrayUnion 
      } = await import('firebase/firestore');

      this.firebase = { 
        collection, doc, setDoc, getDoc, updateDoc, 
        increment, serverTimestamp, arrayUnion 
      };

      const app = getApps().length === 0 ? initializeApp(FIREBASE_CONFIG) : getApp();
      this.db = getFirestore(app);

      this.isInitialized = true;
      console.log(`[PlayerStats] Initialized with player ID: ${this.playerId}`);
    } catch (e) {
      console.error('[PlayerStats] Initialization failed:', e);
    }
  }

  // Start a new play session
  async startSession(callsign: string, heroId: string): Promise<void> {
    if (!this.isInitialized) await this.initialize();

    // Recover any unsaved play time from previous session
    await this.recoverUnsavedPlayTime();

    const { doc, getDoc, setDoc, updateDoc, serverTimestamp } = this.firebase;

    this.currentSession = {
      sessionId: `session_${Date.now()}`,
      startTime: Date.now(),
      heroId,
      gamesPlayed: 0,
      gamesWon: 0,
      kills: 0,
      deaths: 0,
      assists: 0
    };

    try {
      const playerRef = doc(this.db, 'playerStats', this.playerId);
      const playerDoc = await getDoc(playerRef);

      if (playerDoc.exists()) {
        // Update existing player
        await updateDoc(playerRef, {
          callsign,
          country: this.countryInfo.code,
          countryName: this.countryInfo.name,
          lastSeenAt: serverTimestamp(),
          lastSessionStart: serverTimestamp()
        });
      } else {
        // Create new player record
        const newPlayer: Partial<PlayerStats> = {
          odplayerId: this.playerId,
          callsign,
          country: this.countryInfo.code,
          countryName: this.countryInfo.name,
          totalPlayTimeSeconds: 0,
          gamesPlayed: 0,
          gamesWon: 0,
          gamesLost: 0,
          totalKills: 0,
          totalDeaths: 0,
          totalAssists: 0,
          favoriteHero: heroId,
          heroStats: {},
          firstSeenAt: serverTimestamp(),
          lastSeenAt: serverTimestamp(),
          lastSessionStart: serverTimestamp()
        };
        await setDoc(playerRef, newPlayer);

        // Update global stats - new unique player
        await this.incrementGlobalStat('totalUniquePlayers', 1);
        await this.updateCountryStats(this.countryInfo.code, 1);
      }

      console.log(`[PlayerStats] Session started for ${callsign}`);
    } catch (e) {
      console.error('[PlayerStats] Failed to start session:', e);
    }
  }

  // Record a game result
  async recordGameResult(won: boolean, kills: number, deaths: number, assists: number, heroId: string): Promise<void> {
    if (!this.isInitialized || !this.currentSession) return;

    const { doc, updateDoc, increment, serverTimestamp } = this.firebase;

    // Update session data
    this.currentSession.gamesPlayed++;
    if (won) this.currentSession.gamesWon++;
    this.currentSession.kills += kills;
    this.currentSession.deaths += deaths;
    this.currentSession.assists += assists;

    try {
      const playerRef = doc(this.db, 'playerStats', this.playerId);
      
      // Update player stats
      await updateDoc(playerRef, {
        gamesPlayed: increment(1),
        gamesWon: increment(won ? 1 : 0),
        gamesLost: increment(won ? 0 : 1),
        totalKills: increment(kills),
        totalDeaths: increment(deaths),
        totalAssists: increment(assists),
        lastSeenAt: serverTimestamp(),
        [`heroStats.${heroId}.gamesPlayed`]: increment(1),
        [`heroStats.${heroId}.gamesWon`]: increment(won ? 1 : 0),
        [`heroStats.${heroId}.kills`]: increment(kills),
        [`heroStats.${heroId}.deaths`]: increment(deaths)
      });

      // Update global stats
      await this.incrementGlobalStat('totalGamesPlayed', 1);

      console.log(`[PlayerStats] Game recorded: ${won ? 'WIN' : 'LOSS'} - K:${kills} D:${deaths} A:${assists}`);
    } catch (e) {
      console.error('[PlayerStats] Failed to record game:', e);
    }
  }

  // End the current session and record playtime
  async endSession(): Promise<void> {
    if (!this.isInitialized || !this.currentSession) return;

    const { doc, updateDoc, increment, serverTimestamp } = this.firebase;

    const sessionDuration = Math.floor((Date.now() - this.currentSession.startTime) / 1000);

    try {
      const playerRef = doc(this.db, 'playerStats', this.playerId);
      
      await updateDoc(playerRef, {
        totalPlayTimeSeconds: increment(sessionDuration),
        lastSeenAt: serverTimestamp(),
        [`heroStats.${this.currentSession.heroId}.playTimeSeconds`]: increment(sessionDuration)
      });

      // Update global playtime (in hours for display)
      await this.incrementGlobalStat('totalPlayTimeHours', sessionDuration / 3600);

      console.log(`[PlayerStats] Session ended. Duration: ${Math.floor(sessionDuration / 60)} minutes`);
    } catch (e) {
      console.error('[PlayerStats] Failed to end session:', e);
      // If Firebase update fails, save to localStorage as fallback
      this.saveUnsavedPlayTime(sessionDuration, this.currentSession.heroId);
    }

    this.currentSession = null;
  }

  // Save unsaved play time to localStorage (called on page unload when Firebase might not complete)
  saveUnsavedPlayTimeSync(): void {
    if (!this.currentSession) return;
    
    const sessionDuration = Math.floor((Date.now() - this.currentSession.startTime) / 1000);
    if (sessionDuration < 5) return; // Skip very short sessions (< 5 seconds)
    
    this.saveUnsavedPlayTime(sessionDuration, this.currentSession.heroId);
    console.log(`[PlayerStats] Saved unsaved play time to localStorage: ${sessionDuration}s`);
  }

  // Save unsaved play time to localStorage
  private saveUnsavedPlayTime(seconds: number, heroId: string): void {
    try {
      const existing = localStorage.getItem(PlayerStatsServiceClass.UNSAVED_PLAYTIME_KEY);
      const data = existing ? JSON.parse(existing) : { totalSeconds: 0, heroSeconds: {} };
      
      data.totalSeconds = (data.totalSeconds || 0) + seconds;
      data.heroSeconds = data.heroSeconds || {};
      data.heroSeconds[heroId] = (data.heroSeconds[heroId] || 0) + seconds;
      data.playerId = this.playerId;
      data.timestamp = Date.now();
      
      localStorage.setItem(PlayerStatsServiceClass.UNSAVED_PLAYTIME_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('[PlayerStats] Failed to save unsaved play time:', e);
    }
  }

  // Recover and sync any unsaved play time from localStorage
  private async recoverUnsavedPlayTime(): Promise<void> {
    try {
      const saved = localStorage.getItem(PlayerStatsServiceClass.UNSAVED_PLAYTIME_KEY);
      if (!saved) return;
      
      const data = JSON.parse(saved);
      
      // Verify this is for the same player
      if (data.playerId !== this.playerId) {
        localStorage.removeItem(PlayerStatsServiceClass.UNSAVED_PLAYTIME_KEY);
        return;
      }
      
      // Skip if data is too old (> 7 days)
      if (Date.now() - data.timestamp > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(PlayerStatsServiceClass.UNSAVED_PLAYTIME_KEY);
        return;
      }
      
      const { doc, updateDoc, increment, serverTimestamp } = this.firebase;
      const playerRef = doc(this.db, 'playerStats', this.playerId);
      
      // Build update object
      const updates: any = {
        totalPlayTimeSeconds: increment(data.totalSeconds),
        lastSeenAt: serverTimestamp()
      };
      
      // Add hero-specific play time
      for (const [heroId, seconds] of Object.entries(data.heroSeconds || {})) {
        updates[`heroStats.${heroId}.playTimeSeconds`] = increment(seconds as number);
      }
      
      await updateDoc(playerRef, updates);
      
      // Update global playtime
      await this.incrementGlobalStat('totalPlayTimeHours', data.totalSeconds / 3600);
      
      // Clear saved data
      localStorage.removeItem(PlayerStatsServiceClass.UNSAVED_PLAYTIME_KEY);
      console.log(`[PlayerStats] Recovered ${Math.floor(data.totalSeconds / 60)} minutes of unsaved play time`);
    } catch (e) {
      console.warn('[PlayerStats] Failed to recover unsaved play time:', e);
    }
  }

  // Increment a global statistic
  private async incrementGlobalStat(field: string, value: number): Promise<void> {
    const { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } = this.firebase;

    try {
      const globalRef = doc(this.db, 'globalStats', 'stats');
      const globalDoc = await getDoc(globalRef);

      if (globalDoc.exists()) {
        await updateDoc(globalRef, {
          [field]: increment(value),
          lastUpdated: serverTimestamp()
        });
      } else {
        // Create initial global stats document
        await setDoc(globalRef, {
          totalUniquePlayers: field === 'totalUniquePlayers' ? value : 0,
          totalGamesPlayed: field === 'totalGamesPlayed' ? value : 0,
          totalPlayTimeHours: field === 'totalPlayTimeHours' ? value : 0,
          playersByCountry: {},
          lastUpdated: serverTimestamp()
        });
      }
    } catch (e) {
      console.error('[PlayerStats] Failed to update global stat:', e);
    }
  }

  // Update country player count
  private async updateCountryStats(countryCode: string, delta: number): Promise<void> {
    const { doc, updateDoc, increment, serverTimestamp } = this.firebase;

    try {
      const globalRef = doc(this.db, 'globalStats', 'stats');
      await updateDoc(globalRef, {
        [`playersByCountry.${countryCode}`]: increment(delta),
        lastUpdated: serverTimestamp()
      });
    } catch (e) {
      console.error('[PlayerStats] Failed to update country stats:', e);
    }
  }

  // Get current player's stats (for display)
  async getMyStats(): Promise<PlayerStats | null> {
    if (!this.isInitialized) await this.initialize();

    const { doc, getDoc } = this.firebase;

    try {
      const playerRef = doc(this.db, 'playerStats', this.playerId);
      const playerDoc = await getDoc(playerRef);
      
      if (playerDoc.exists()) {
        return playerDoc.data() as PlayerStats;
      }
    } catch (e) {
      console.error('[PlayerStats] Failed to get stats:', e);
    }

    return null;
  }

  // Get global stats (for webpage display)
  async getGlobalStats(): Promise<GlobalStats | null> {
    if (!this.isInitialized) await this.initialize();

    const { doc, getDoc } = this.firebase;

    try {
      const globalRef = doc(this.db, 'globalStats', 'stats');
      const globalDoc = await getDoc(globalRef);
      
      if (globalDoc.exists()) {
        return globalDoc.data() as GlobalStats;
      }
    } catch (e) {
      console.error('[PlayerStats] Failed to get global stats:', e);
    }

    return null;
  }

  // Get player ID (for external use)
  getPlayerId(): string {
    return this.playerId;
  }

  // Check if session is active
  hasActiveSession(): boolean {
    return this.currentSession !== null;
  }
}

// Singleton instance
export const PlayerStatsService = new PlayerStatsServiceClass();
